import AddIcon from '@/assets/add.svg?react'
import JellowLogo from '@/assets/jellow-logo.svg?react'
import JikeLogo from '@/assets/jike-logo.svg?react'
import { MOBILE_SHELL_SPRING_TRANSITION } from '@/constants/motion'
import { useIsMobileShellHidden } from '@/utils/dom'
import { useIsDarkTheme } from '@/utils/hooks/useTheme'
import { ActionIcon, Anchor, Box, Group, Image, Stack } from '@mantine/core'
import { modals } from '@mantine/modals'
import clsx from 'clsx'
import { AnimatePresence, motion } from 'motion/react'
import type { HTMLAttributes } from 'react'
import ClickWrapper from '../ClickWrapper'
import JikeYellowLogoIcon from '../Icons/JikeYellowLogoIcon'
import { showPostFormModal } from '../Post/utils'
import { SIDEBAR_WIDTH } from './config'
import styles from './index.module.css'
import TabBar from './TabBar'

export interface SidebarProps extends HTMLAttributes<HTMLDivElement> {}

export default function Sidebar({ ...props }: SidebarProps) {
	const isDarkMode = useIsDarkTheme()
	const isMobileShellHidden = useIsMobileShellHidden()

	const renderCreateButton = (className?: string, size = 40) => {
		return (
			<ActionIcon
				aria-label="创建动态"
				onClick={() => showPostFormModal()}
				size={size}
				radius="50%"
				className={className}
			>
				<AddIcon className={styles.addIcon} />
			</ActionIcon>
		)
	}

	const renderDesktopTabs = () => {
		return (
			<Stack gap={32} justify="center" align="center" flex={1}>
				<TabBar />
				{renderCreateButton()}
			</Stack>
		)
	}

	return (
		<>
			<motion.div
				className={styles.mobileCreateButtonWrapper}
				initial={false}
				animate={{
					y: isMobileShellHidden ? 'var(--tab-bar-fab-shift-y)' : 0,
				}}
				transition={MOBILE_SHELL_SPRING_TRANSITION}
			>
				{renderCreateButton(styles.mobileCreateButton, 56)}
			</motion.div>

			<motion.div
				className={styles.mobileTabBarShell}
				initial={false}
				animate={{
					y: isMobileShellHidden ? '100%' : '0%',
				}}
				style={{
					pointerEvents: isMobileShellHidden ? 'none' : 'auto',
				}}
				transition={MOBILE_SHELL_SPRING_TRANSITION}
			>
				<Group align="center" className={styles.mobileTabBar}>
					<TabBar className={styles.tabBar} style={{ flexDirection: 'row' }} />
				</Group>
			</motion.div>

			<Stack
				visibleFrom="sm"
				className={styles.desktopStack}
				align="center"
				pos="fixed"
				top={0}
				left={0}
				w={SIDEBAR_WIDTH}
				h="100vh"
				p={20}
				pb="calc(20px + env(safe-area-inset-bottom, 0px))"
				bg="var(--bg-body-1)"
			>
				<Anchor unstyled href="/" aria-label="即刻 Logo" className={`jike-logo ${styles.logoLink}`}>
					<AnimatePresence mode="wait" initial={false}>
						{isDarkMode ? (
							<motion.div
								key="dark"
								initial={{ y: 10, opacity: 0 }}
								animate={{ y: 0, opacity: 1 }}
								exit={{ y: -10, opacity: 0 }}
								transition={{ duration: 0.2, type: 'spring', damping: 20 }}
							>
								<JellowLogo className={styles.logo} />
							</motion.div>
						) : (
							<motion.div
								key="light"
								initial={{ y: 10, opacity: 0 }}
								animate={{ y: 0, opacity: 1 }}
								exit={{ y: -10, opacity: 0 }}
								transition={{ duration: 0.2, type: 'spring', damping: 20 }}
							>
								<JikeLogo className={styles.logoLight} />
							</motion.div>
						)}
					</AnimatePresence>
				</Anchor>
				{renderDesktopTabs()}
				<Box className={clsx('sidebar-footer', styles.footer)}>
					<ClickWrapper
						data-anonymous-gated-interaction
						whileTap={{
							scale: 0.98,
						}}
						onClick={() => {
							modals.open({
								size: 'auto',
								styles: {
									header: {
										backgroundColor: 'unset',
									},
									body: {
										padding: 0,
									},
									content: {
										backgroundColor: 'unset',
									},
								},
								withCloseButton: false,
								children: (
									<Image
										maw="90vw"
										mah="90vh"
										src="https://cdnv2.ruguoapp.com/FlHEfqaOMiWvKDIr2WyK-c9XFqNX.png"
										radius={20}
										fit="contain"
									/>
								),
							})
						}}
					>
						<JikeYellowLogoIcon className={styles.footerIcon} />
					</ClickWrapper>
				</Box>
			</Stack>
		</>
	)
}
